import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';
const POLL_MS = 60 * 1000; // check every minute
const LS_KEY = 'mdb_app_version';

/**
 * Force a full refresh:
 * - unregister any service worker
 * - clear all caches exposed to the page (Cache API)
 * - reload with a cache-busting query string
 */
export const hardRefresh = async () => {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if (window.caches && caches.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* ignore */
  }
  const sep = window.location.href.includes('?') ? '&' : '?';
  window.location.replace(`${window.location.pathname}${sep}_v=${Date.now()}${window.location.hash}`);
};

export const CacheBuster = () => {
  const bootVersionRef = useRef(null);
  const toastShownRef = useRef(false);
  const [, setForce] = useState(0);

  const checkVersion = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/version`, {
        withCredentials: false,
        headers: { 'Cache-Control': 'no-cache' },
        params: { _t: Date.now() },
      });
      const remoteVersion = res.data?.version;
      if (!remoteVersion) return;

      if (!bootVersionRef.current) {
        bootVersionRef.current = remoteVersion;
        localStorage.setItem(LS_KEY, remoteVersion);
        return;
      }

      if (remoteVersion !== bootVersionRef.current && !toastShownRef.current) {
        toastShownRef.current = true;
        localStorage.setItem(LS_KEY, remoteVersion);
        toast.message('Nouvelle version disponible', {
          description: 'Touchez pour rafraîchir et charger les derniers correctifs.',
          duration: Infinity,
          action: {
            label: 'Rafraîchir',
            onClick: () => hardRefresh(),
          },
        });
        setForce((x) => x + 1);
      }
    } catch {
      /* ignore network errors */
    }
  }, []);

  useEffect(() => {
    checkVersion();
    const id = setInterval(checkVersion, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkVersion();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [checkVersion]);

  return null;
};

export const HardRefreshButton = ({ className = '' }) => (
  <button
    type="button"
    onClick={hardRefresh}
    className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white transition-colors ${className}`}
    data-testid="hard-refresh-button"
    title="Vider le cache et recharger l'application"
  >
    <RefreshCw className="w-3.5 h-3.5" />
    Vider le cache
  </button>
);
